import {
  defaultUserChoices,
  loadUserChoices,
  saveUserChoices,
  type LocalUserChoices,
} from '@livekit/components-core';

export type CalendarDefaultView = 'week' | 'month';
export type CalendarDefaultDuration = 15 | 30 | 60;

export type ExtendedAppSettings = {
  audioOutputDeviceId: string;
  volume: number;
  calendarDefaultView: CalendarDefaultView;
  calendarDefaultDurationMinutes: CalendarDefaultDuration;
};

export type AppSettings = LocalUserChoices & ExtendedAppSettings;

const EXTENDED_KEY = 'lk-meet-app-settings';
export const APP_SETTINGS_EVENT = 'lk-meet-app-settings';

const EXTENDED_DEFAULTS: ExtendedAppSettings = {
  audioOutputDeviceId: 'default',
  volume: 80,
  calendarDefaultView: 'week',
  calendarDefaultDurationMinutes: 60,
};

function loadExtended(): ExtendedAppSettings {
  if (typeof window === 'undefined') {
    return { ...EXTENDED_DEFAULTS };
  }
  try {
    const raw = window.localStorage.getItem(EXTENDED_KEY);
    if (!raw) return { ...EXTENDED_DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<ExtendedAppSettings>;
    return {
      ...EXTENDED_DEFAULTS,
      ...parsed,
      calendarDefaultView:
        parsed.calendarDefaultView === 'month' ? 'month' : EXTENDED_DEFAULTS.calendarDefaultView,
      calendarDefaultDurationMinutes: [15, 30, 60].includes(
        parsed.calendarDefaultDurationMinutes as number,
      )
        ? (parsed.calendarDefaultDurationMinutes as CalendarDefaultDuration)
        : EXTENDED_DEFAULTS.calendarDefaultDurationMinutes,
      volume:
        typeof parsed.volume === 'number'
          ? Math.min(100, Math.max(0, parsed.volume))
          : EXTENDED_DEFAULTS.volume,
    };
  } catch {
    return { ...EXTENDED_DEFAULTS };
  }
}

function saveExtended(settings: ExtendedAppSettings) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(EXTENDED_KEY, JSON.stringify(settings));
}

function pickLocalUserChoices(settings: AppSettings): LocalUserChoices {
  return {
    username: settings.username,
    audioEnabled: settings.audioEnabled,
    videoEnabled: settings.videoEnabled,
    audioDeviceId: settings.audioDeviceId,
    videoDeviceId: settings.videoDeviceId,
  };
}

function pickExtended(settings: AppSettings): ExtendedAppSettings {
  return {
    audioOutputDeviceId: settings.audioOutputDeviceId,
    volume: settings.volume,
    calendarDefaultView: settings.calendarDefaultView,
    calendarDefaultDurationMinutes: settings.calendarDefaultDurationMinutes,
  };
}

export function loadAppSettings(defaults?: Partial<AppSettings>): AppSettings {
  const lk = loadUserChoices(defaults);
  const extended = loadExtended();
  const base: AppSettings = { ...lk, ...extended };
  if (!defaults) {
    return base;
  }

  const merged: AppSettings = { ...base, ...defaults };
  const defaultUsername = defaults.username?.trim();
  if (defaultUsername) {
    merged.username = defaultUsername;
  } else {
    merged.username = base.username;
  }
  return merged;
}

export function saveAppSettings(settings: AppSettings, options?: { skipUsername?: boolean }) {
  const lk = pickLocalUserChoices(settings);
  if (options?.skipUsername) {
    const current = loadUserChoices();
    lk.username = current.username;
  }
  saveUserChoices(lk);
  saveExtended(pickExtended(settings));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(APP_SETTINGS_EVENT));
  }
}

export function patchAppSettings(
  patch: Partial<AppSettings>,
  options?: { skipUsername?: boolean },
): AppSettings {
  const next = { ...loadAppSettings(), ...patch };
  saveAppSettings(next, options);
  return next;
}

export function subscribeAppSettings(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(APP_SETTINGS_EVENT, listener);
  return () => window.removeEventListener(APP_SETTINGS_EVENT, listener);
}

export function getDefaultAppSettings(): AppSettings {
  return {
    ...defaultUserChoices,
    ...EXTENDED_DEFAULTS,
  };
}

export function volumeToPlaybackGain(volume: number): number {
  return Math.min(100, Math.max(0, volume)) / 100;
}

export function getAudioOutputDeviceId(settings: AppSettings): string | undefined {
  const deviceId = settings.audioOutputDeviceId?.trim();
  if (!deviceId || deviceId === 'default') return undefined;
  return deviceId;
}

export function getAudioCaptureOptions(
  settings: AppSettings,
  overrides?: Partial<Pick<AppSettings, 'audioDeviceId'>>,
) {
  const deviceId = overrides?.audioDeviceId ?? settings.audioDeviceId;
  return {
    deviceId: deviceId && deviceId !== 'default' ? deviceId : undefined,
  };
}
